/**
 * @module TabsItem
 *
 * @see https://0.vuetifyjs.com/components/disclosure/tabs
 *
 * @remarks
 * Individual tab trigger that registers with the parent TabsRoot.
 * Provides complete ARIA attributes, roving tabindex, and keyboard
 * handling for accessibility. Supports both automatic and manual
 * activation modes.
 *
 * @example
 * ```ts
 * // Using slot props for conditional styling
 * h(Tabs.Item, { value: 'profile' }, {
 *   default: ({ isSelected }) => h('button', {
 *     class: isSelected ? 'border-b-2 border-blue-500' : ''
 *   }, 'Profile')
 * })
 * ```
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Context
  import { useTabsRoot } from './TabsRoot.vue'

  // Transformers
  import { toElement } from '#v0/composables/toElement'

  // Utilities
  import { mergeProps, nextTick, onBeforeUnmount, toRef, toValue, useAttrs, useTemplateRef } from 'vue'

  // Types
  import type { AtomExpose, AtomProps } from '#v0/components/Atom'
  import type { ID } from '#v0/types'
  import type { MaybeRefOrGetter } from 'vue'

  export interface TabsItemProps<V = unknown> extends AtomProps {
    /** Unique identifier (auto-generated if not provided) */
    id?: ID
    /** Value associated with this tab (used to match with TabsPanel) */
    value?: V
    /** Disables this specific tab */
    disabled?: boolean | MaybeRefOrGetter<boolean>
    /** Namespace for dependency injection */
    namespace?: string
    /** Accessible label for this tab */
    ariaLabel?: string
    /** ID of element that labels this tab */
    ariaLabelledby?: string
    /** ID of element that describes this tab */
    ariaDescribedby?: string
    /** Focus target when renderless / `:as="null"`. Defaults to the Atom host. */
    el?: MaybeRefOrGetter<Element | null | undefined>
  }

  export interface TabsItemSlotProps {
    /** Unique identifier */
    id: string
    /** Whether this tab is currently selected */
    isSelected: boolean
    /** Whether this tab is disabled */
    isDisabled: boolean
    /** Select this tab */
    select: () => void
    /** Attributes to bind to the tab element */
    attrs: {
      'id': string
      'role': 'tab'
      'tabindex': 0 | -1
      'aria-selected': boolean
      'aria-controls': string
      'aria-disabled': boolean
      'aria-label': string | undefined
      'aria-labelledby': string | undefined
      'aria-describedby': string | undefined
      'data-selected': true | undefined
      'data-disabled': true | undefined
      'disabled': boolean | undefined
      'type': 'button' | undefined
      'onClick': () => void
      'onKeydown': (e: KeyboardEvent) => void
      'onFocus': () => void
    }
  }
</script>

<script lang="ts" setup generic="V = unknown">
  defineOptions({ name: 'TabsItem', inheritAttrs: false })

  const attrs = useAttrs()
  const rootRef = useTemplateRef<AtomExpose>('root')

  defineSlots<{
    default: (props: TabsItemSlotProps) => any
  }>()

  const {
    as = 'button',
    renderless,
    id,
    value,
    disabled,
    namespace = 'v0:tabs',
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    el: _el,
  } = defineProps<TabsItemProps<V>>()

  const tabs = useTabsRoot(namespace)

  const el = toRef(() => toElement(_el) ?? toElement(rootRef.value?.element) ?? undefined)
  const ticket = tabs.register({ id, value, disabled: () => toValue(disabled) ?? false, el })

  const isDisabled = toRef(() => toValue(ticket.disabled) || toValue(tabs.disabled))

  const tabId = toRef(() => `${tabs.rootId}-tab-${ticket.id}`)
  const panelId = toRef(() => `${tabs.rootId}-panel-${ticket.id}`)

  onBeforeUnmount(() => {
    tabs.unregister(ticket.id)
  })

  function focusSelectedTab () {
    nextTick(() => {
      const selected = tabs.selectedItem.value
      if (selected) {
        (toElement(selected.el) as HTMLElement | undefined)?.focus()
      }
    })
  }

  // Manual mode: move focus without changing selection
  function focusAdjacent (direction: 1 | -1) {
    const all = tabs.values()
    const current = all.findIndex(t => t.id === ticket.id)
    const length = all.length
    const circular = tabs.circular.value
    let index = current + direction
    let hops = 0

    while (hops < length) {
      if (circular) {
        index = ((index % length) + length) % length
      } else if (index < 0 || index >= length) {
        return
      }
      const item = all[index]
      if (item && !toValue(item.disabled)) {
        nextTick(() => (toElement(item.el) as HTMLElement | undefined)?.focus())
        return
      }
      index += direction
      hops++
    }
  }

  function focusEdge (edge: 'first' | 'last') {
    const item = tabs.seek(edge)
    if (item) nextTick(() => (toElement(item.el) as HTMLElement | undefined)?.focus())
  }

  function isRtl (e: KeyboardEvent) {
    const el = e.currentTarget
    if (!(el instanceof HTMLElement)) return false
    return getComputedStyle(el).direction === 'rtl'
  }

  function onKeydown (e: KeyboardEvent) {
    const orientation = tabs.orientation.value
    const isHorizontal = orientation === 'horizontal'
    const manual = tabs.activation.value === 'manual'
    const rtl = isHorizontal && isRtl(e)
    const forward = rtl ? 'ArrowLeft' : 'ArrowRight'
    const backward = rtl ? 'ArrowRight' : 'ArrowLeft'

    // Arrow key navigation
    if (
      (isHorizontal && e.key === forward) ||
      (!isHorizontal && e.key === 'ArrowDown')
    ) {
      e.preventDefault()
      if (manual) {
        focusAdjacent(1)
      } else {
        tabs.next()
        focusSelectedTab()
      }
      return
    }

    if (
      (isHorizontal && e.key === backward) ||
      (!isHorizontal && e.key === 'ArrowUp')
    ) {
      e.preventDefault()
      if (manual) {
        focusAdjacent(-1)
      } else {
        tabs.prev()
        focusSelectedTab()
      }
      return
    }

    // Home/End navigation
    if (e.key === 'Home') {
      e.preventDefault()
      if (manual) {
        focusEdge('first')
      } else {
        tabs.first()
        focusSelectedTab()
      }
      return
    }

    if (e.key === 'End') {
      e.preventDefault()
      if (manual) {
        focusEdge('last')
      } else {
        tabs.last()
        focusSelectedTab()
      }
      return
    }

    // Manual activation: Enter/Space selects the focused tab
    if (manual && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      ticket.select()
    }
  }

  function onClick () {
    if (!isDisabled.value) {
      ticket.select()
    }
  }

  function onFocus () {
    // Automatic activation: select on focus
    if (tabs.activation.value === 'automatic' && !isDisabled.value) {
      ticket.select()
    }
  }

  const slotProps = toRef((): TabsItemSlotProps => ({
    id: String(ticket.id),
    isSelected: toValue(ticket.isSelected),
    isDisabled: toValue(isDisabled),
    select: ticket.select,
    attrs: {
      'id': tabId.value,
      'role': 'tab',
      'tabindex': toValue(ticket.isSelected) ? 0 : -1,
      'aria-selected': toValue(ticket.isSelected),
      'aria-controls': panelId.value,
      'aria-disabled': toValue(isDisabled),
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      'data-selected': toValue(ticket.isSelected) || undefined,
      'data-disabled': toValue(isDisabled) || undefined,
      'disabled': as === 'button' ? toValue(isDisabled) : undefined,
      'type': as === 'button' ? 'button' : undefined,
      'onClick': onClick,
      'onKeydown': onKeydown,
      'onFocus': onFocus,
    },
  }))
</script>

<template>
  <Atom
    ref="root"
    v-bind="mergeProps(attrs, slotProps.attrs)"
    :as
    :renderless
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
